import { Link, useNavigate } from "react-router-dom";
import H3 from "../../../components/Titles/h3";
import DescriptionSplit from "../../../components/Description/description-split";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightLong, faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import ButtonFit from "../../../components/Buttons/button-fit";
import { toImg } from '../../../utils/img';
import { useTranslation } from "react-i18next";

function formatDateISO(d, lang) {
    try {
        const dt = new Date(d);
        const s = dt.toLocaleDateString(lang, {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
        // Capitalize first letter to match the mockup (e.g., "Dimanche ...")
        return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
    } catch {
        return "";
    }
}

const pick = (obj, lang) => (obj?.[lang] ?? obj?.fr ?? obj?.en ?? obj?.de ?? "");

export default function EventPublicCard({ ev, lang = "fr-FR" }) {
    const { t } = useTranslation();
    const title = pick(ev?.name, lang);
    const desc = pick(ev?.shortDescription, lang);
    const nextDate = ev?.dates?.length ? formatDateISO(ev.dates[0], lang) : null;
    const navigate = useNavigate();

    return (
        <article
            className="bg-white shadow overflow-hidden flex flex-col">
            {/* Image with subtle bottom gradient + thin gold divider */}
            {ev?.cardImage && (
                <>
                    <div className="relative h-56 border border-main border-b-0">
                        <img
                            src={toImg(ev.cardImage)}
                            alt={title}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/35 to-transparent" />
                    </div>
                </>
            )}

            {/* Body */}
            <div className="p-6 flex flex-col gap-4 flex-1 border border-main">

                <H3 title={title} />

                <DescriptionSplit description={desc} />

                {/* Next date */}

                {nextDate && (
                    <div className="mt-1 grid gap-3 items-start">
                        <div className="leading-tight">
                            <div className="flex items-center">
                                <span
                                    aria-hidden
                                    className="inline-flex items-center justify-center w-7 h-7 rounded-full text-main text-xs"
                                >
                                    <FontAwesomeIcon icon={faCalendarDays} className="text-[13px]" />
                                </span>
                                <span className="text-[13px] text-description">
                                    {t("events.eventPublic.nextDate")}
                                </span>
                            </div>
                            <div className="text-[15px] font-medium text-description opacity-60">
                                {nextDate}
                            </div>
                        </div>
                    </div>
                )}

                {/* Tags */}
                {!!ev?.tags?.length && (
                    <div className="mt-1 flex flex-wrap gap-2">
                        {ev.tags.map((t) => (
                            <span
                                key={t}
                                className="px-3 py-1 rounded-full text-[11px] border border-main bg-white text-description opacity-70 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.6)]">
                                {t}
                            </span>
                        ))}
                    </div>
                )}

                {/* CTA */}
                <div className="mt-2">
                    <Link
                        to={`/events/${ev?.slug ?? ""}`}>
                        <ButtonFit
                            text={t("events.eventPublic.viewDetails")}
                            action={ev?.slug ?? ""}
                            onClick={(slug) => navigate(`/events/${slug}`)}
                        >
                            <FontAwesomeIcon
                                icon={faArrowRightLong}
                                className="ml-2"
                                aria-hidden="true"
                            />
                        </ButtonFit>
                    </Link>
                </div>
            </div>
        </article>
    );
}